import React, { Fragment, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import InternalFilesView from '../Component'
import {
  BUTTON_TYPES,
  disabledDeleteButton,
  disabledDownloadButton,
  disabledUploadButton,
  TYPES,
} from './options'
import LoadingIndicator from '../../../../../Components/LoadingIndicator/LoadingIndicator'
import {
  deleteDigitalProofFromPrinterDocument,
  downloadFile,
  downloadFlyerDesign,
  downloadFlyersCodes,
  downloadInsertsCampaignTrackingData,
  downloadSummary,
  handleGetAndUploadFile,
  onCloseReactModal,
} from './utils'
import { apiClient } from '../../../../../Module/API/Client'
import { useParams } from 'react-router-dom'
import { authorization } from '../../../../../Module/Auth/Auth'
import {
  createCampaignDocument,
  getData,
  updateCampaignDocument,
} from './graphQL'
import useGraphQLFetch from '../../../../../Utils/Hooks/useGraphQLFetch'
import { convertArrayToHashMap } from '../../../../../shared/libraries/dataSets'
import {
  CAMPAIGN_DOCUMENT_TYPES,
  CAMPAIGN_ENUM_TYPES,
  CAMPAIGN_INTERNAL_STATUSES,
  CAMPAIGN_LOG_ENUM_TYPES,
} from '../../../../../shared/constants'
import { updateCampaignsCampaignBuilderData } from '../../../../Country/Page/Pipeline/PipelineView/graphQL'
import { showReduxModal } from '../../../../../Reducers/UI/actions'
import Loader from '../../Codes/components/CommonComponents/Loader'
import { insertCampaignLogs } from '../../../../../Logs/campaign/gql'

const InternalFilesContainer = () => {
  const [buttonsActions, setButtonsActions] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isDownloading, setIsDownloading] = useState(false)
  const [campaign, setCampaign] = useState(null)
  const { campaignId } = useParams()

  const user = authorization.getUser()
  const dispatch = useDispatch()

  const { data, isLoaded, refetch } = useGraphQLFetch({
    fetchFn: getData,
    defaultFetchArgs: { campaignId },
    mapResponse: data => ({
      contracts: convertArrayToHashMap(data.getCampaignDocuments, 'type'),
      campaign: {
        ...data.campaign,
        ...data.campaignExtraData,
        ...data.campaignFlyerInfo,
        clientCodes: data.campaignClientCodes
          ? [...data.campaignClientCodes.clientCodes]
          : [],
        internalCodes: data.campaignInternalCodes
          ? [...data.campaignInternalCodes.internalCodes]
          : [],
      },
    }),
  })

  const handleUploadAndSaveFileToDB = async ({
    file,
    campaignId,
    userId,
    type,
  }) => {
    setIsLoading(true)

    const url = await apiClient.signFileAndUploadToS3({
      body: file,
      type,
      payload: { campaignId },
    })

    await createCampaignDocument({
      campaignId,
      uploaderId: userId,
      url,
      type,
      fileName: file.name,
    })

    refetch({ campaignId })
  }

  const getButtons = ({ contracts, type, campaign }) => {
    const contract = contracts[type]
    const {
      campaignId,
      internalStatus,
      createdAt,
      name: campaignName,
      countryId,
      type: campaignType,
      internalCodes,
      clientCodes,
      flyerName,
      s3Link,
      couponType,
    } = campaign

    const hasCampaignFlyerInfo = flyerName && s3Link

    const isCampaignCreatedFromCampaignBuilder = !!createdAt

    // const isUploadedDigitalProofSignedByClientDocument =
    //   contracts[CAMPAIGN_DOCUMENT_TYPES.DIGITAL_PROOF_SIGNED_BY_CLIENT]

    // const isUploadedDigitalProofFromPrinterDocument =
    //   contracts[TYPES.DIGITAL_PROOF_FROM_PRINTER]

    const disableDeleteButton =
      isCampaignCreatedFromCampaignBuilder &&
      internalStatus === CAMPAIGN_INTERNAL_STATUSES.ACTIVE

    const isHaveCodes =
      [
        CAMPAIGN_INTERNAL_STATUSES.PENDING_DIGITAL_PROOF,
        CAMPAIGN_INTERNAL_STATUSES.GENERATING_DIGITAL_PROOF,
        CAMPAIGN_INTERNAL_STATUSES.PAYMENT,
        CAMPAIGN_INTERNAL_STATUSES.ACTIVE,
      ].includes(internalStatus) &&
      internalCodes.length &&
      clientCodes.length

    if (TYPES.DIGITAL_PROOF_FROM_PRINTER === type) {
      if (contract) {
        return {
          success: true,
          [BUTTON_TYPES.UPLOAD]: disabledUploadButton,
          [BUTTON_TYPES.DOWNLOAD]: {
            label: 'Download',
            onClick: () => {
              downloadFile(contract.url, contract.fileName)
            },
            disabled: false,
          },
          [BUTTON_TYPES.DELETE]: {
            label: 'Delete',
            onClick: async () => {
              dispatch(
                showReduxModal({
                  title: `Confirmation needed`,
                  children: (
                    <div style={{ textAlign: 'center' }}>
                      Delete delete digital proof from printer document
                    </div>
                  ),
                  onConfirm: async () => {
                    setIsLoading(true)
                    await deleteDigitalProofFromPrinterDocument({
                      contractId: contract.id,
                      campaignId,
                      isCampaignCreatedFromCampaignBuilder,
                    })
                    await insertCampaignLogs([
                      {
                        campaignId,
                        type: CAMPAIGN_LOG_ENUM_TYPES.DELETE_DIGITAL_PROOF,
                      },
                    ])
                    onCloseReactModal()
                    refetch({ campaignId })
                  },
                  onCancel: onCloseReactModal,
                }),
              )
            },
            disabled: internalStatus === CAMPAIGN_INTERNAL_STATUSES.ACTIVE,
          },
        }
      } else {
        return {
          success: false,
          [BUTTON_TYPES.UPLOAD]: {
            label: 'Upload',
            onClick: async () => {
              if (
                isCampaignCreatedFromCampaignBuilder &&
                internalStatus ===
                  CAMPAIGN_INTERNAL_STATUSES.GENERATING_DIGITAL_PROOF
              ) {
                await updateCampaignsCampaignBuilderData({
                  input: {
                    campaignId,
                    internalStatus: CAMPAIGN_INTERNAL_STATUSES.PAYMENT,
                  },
                })
              }
              await insertCampaignLogs([
                {
                  campaignId,
                  type: CAMPAIGN_LOG_ENUM_TYPES.UPLOAD_DIGITAL_PROOF,
                },
              ])
              await handleGetAndUploadFile(handleUploadAndSaveFileToDB, {
                campaignId,
                userId: user.id,
                type,
              })
            },
            disabled:
              isCampaignCreatedFromCampaignBuilder &&
              internalStatus !==
                CAMPAIGN_INTERNAL_STATUSES.GENERATING_DIGITAL_PROOF,
          },
          [BUTTON_TYPES.DOWNLOAD]: disabledDownloadButton,
          [BUTTON_TYPES.DELETE]: disabledDeleteButton,
        }
      }
    }

    // if (TYPES.DIGITAL_PROOF_SENT_TO_CLIENT === type) {
    //   return {
    //     success: isDigitalProofSentToClient,
    //     [BUTTON_TYPES.SENT]: {
    //       label: isDigitalProofSentToClient ? 'Unsent' : 'Sent',
    //       onClick: async () => {
    //         setIsLoading(true)
    //         await sentDigitalProofToClient({
    //           campaignId,
    //           isDigitalProofSentToClient,
    //           isCampaignCreatedFromCampaignBuilder,
    //         })
    //         refetch({ campaignId })
    //       },
    //       disabled:
    //         isUploadedDigitalProofSignedByClientDocument ||
    //         !isUploadedDigitalProofFromPrinterDocument,
    //     },
    //   }
    // }
    //
    // if (TYPES.DIGITAL_PROOF_SIGNED_BY_CLIENT === type) {
    //   if (contract) {
    //     return {
    //       success: true,
    //       [BUTTON_TYPES.UPLOAD]: disabledUploadButton,
    //       [BUTTON_TYPES.DOWNLOAD]: {
    //         label: 'Download',
    //         onClick: () => {
    //           downloadFile(contract.url, contract.fileName)
    //         },
    //         disabled: false,
    //       },
    //       [BUTTON_TYPES.DELETE]: {
    //         label: 'Delete',
    //         onClick: async () => {
    //           dispatch(
    //             showReduxModal({
    //               title: `Confirmation needed`,
    //               children: (
    //                 <div style={{ textAlign: 'center' }}>
    //                   Delete delete digital proof signed by client document
    //                 </div>
    //               ),
    //               onConfirm: async () => {
    //                 setIsLoading(true)
    //                 await deleteDigitalProofSignedByClient({
    //                   contractId: contract.id,
    //                   campaignId,
    //                   isCampaignCreatedFromCampaignBuilder,
    //                 })
    //                 onCloseReactModal()
    //                 refetch({ campaignId })
    //               },
    //               onCancel: onCloseReactModal,
    //             }),
    //           )
    //         },
    //         disabled: disableDeleteButton || false,
    //       },
    //     }
    //   } else {
    //     return {
    //       success: false,
    //       [BUTTON_TYPES.UPLOAD]: {
    //         label: 'Upload',
    //         onClick: async () => {
    //           if (isCampaignCreatedFromCampaignBuilder) {
    //             await updateCampaignsCampaignBuilderData({
    //               input: {
    //                 campaignId,
    //                 internalStatus: CAMPAIGN_INTERNAL_STATUSES.PAYMENT,
    //               },
    //             })
    //           }
    //           await handleGetAndUploadFile(handleUploadAndSaveFileToDB, {
    //             campaignId,
    //             userId: user.id,
    //             type,
    //           })
    //         },
    //         disabled: !isDigitalProofSentToClient,
    //       },
    //       [BUTTON_TYPES.DOWNLOAD]: disabledDownloadButton,
    //       [BUTTON_TYPES.DELETE]: disabledDeleteButton,
    //     }
    //   }
    // }

    if (TYPES.STATEMENT_OF_WORK === type) {
      if (contract) {
        return {
          success: true,
          [BUTTON_TYPES.UPLOAD]: disabledUploadButton,
          [BUTTON_TYPES.DOWNLOAD]: {
            label: 'Download',
            onClick: () => {
              downloadFile(contract.url, contract.fileName)
            },
            disabled: false,
          },
          [BUTTON_TYPES.DELETE]: {
            label: 'Delete',
            onClick: async () => {
              dispatch(
                showReduxModal({
                  title: `Confirmation needed`,
                  children: (
                    <div style={{ textAlign: 'center' }}>
                      Delete delete statement of work document
                    </div>
                  ),
                  onConfirm: async () => {
                    setIsLoading(true)
                    await updateCampaignDocument({
                      id: contract.id,
                      isActual: false,
                    })
                    await insertCampaignLogs([
                      {
                        campaignId,
                        type: CAMPAIGN_LOG_ENUM_TYPES.DELETE_STATEMENT_OF_WORK,
                      },
                    ])
                    onCloseReactModal()
                    refetch({ campaignId })
                  },
                  onCancel: onCloseReactModal,
                }),
              )
            },
            disabled: disableDeleteButton || false,
          },
        }
      } else {
        return {
          success: false,
          [BUTTON_TYPES.UPLOAD]: {
            label: 'Upload',
            onClick: async () => {
              await insertCampaignLogs([
                {
                  campaignId,
                  type: CAMPAIGN_LOG_ENUM_TYPES.UPLOAD_STATEMENT_OF_WORK,
                },
              ])
              await handleGetAndUploadFile(handleUploadAndSaveFileToDB, {
                campaignId,
                userId: user.id,
                type,
              })
            },
            disabled: false,
          },
          [BUTTON_TYPES.DOWNLOAD]: disabledDownloadButton,
          [BUTTON_TYPES.DELETE]: disabledDeleteButton,
        }
      }
    }

    if (TYPES.DOWNLOAD_ALL === type) {
      return {
        off: !isCampaignCreatedFromCampaignBuilder,
        [BUTTON_TYPES.DOWNLOAD]: {
          label: 'Download',
          onClick: async () => {
            setIsDownloading(true)
            if (hasCampaignFlyerInfo) {
              await downloadFlyerDesign({ flyerName, s3Link })
            }
            await downloadFlyersCodes({
              clientCodes,
              internalCodes,
              campaignName,
              couponType,
            })
            await downloadSummary({
              campaignId,
              countryId,
              campaignType,
              campaignName,
              clientCodes,
              internalCodes,
            })
            setIsDownloading(false)
          },
          disabled:
            !isCampaignCreatedFromCampaignBuilder ||
            !hasCampaignFlyerInfo ||
            !isHaveCodes,
        },
      }
    }

    if (TYPES.DOWNLOAD_FLYER_DESIGN === type) {
      return {
        off: !isCampaignCreatedFromCampaignBuilder,
        [BUTTON_TYPES.DOWNLOAD]: {
          label: 'Download',
          onClick: async () => {
            setIsDownloading(true)
            if (hasCampaignFlyerInfo) {
              await downloadFlyerDesign({ flyerName, s3Link })
            }
            setIsDownloading(false)
          },
          disabled:
            !isCampaignCreatedFromCampaignBuilder || !hasCampaignFlyerInfo,
        },
      }
    }

    if (TYPES.DOWNLOAD_FLYER_CODES === type) {
      return {
        off: !isCampaignCreatedFromCampaignBuilder,
        [BUTTON_TYPES.DOWNLOAD]: {
          label: 'Download',
          onClick: async () => {
            setIsDownloading(true)
            if (campaign && campaign.type === CAMPAIGN_ENUM_TYPES.INSERTS) {
              await downloadInsertsCampaignTrackingData({
                campaignId,
                campaignName,
              })
            } else {
              await downloadFlyersCodes({
                clientCodes,
                internalCodes,
                campaignName,
                couponType,
              })
            }
            setIsDownloading(false)
          },
          disabled: !isCampaignCreatedFromCampaignBuilder || !isHaveCodes,
        },
      }
    }

    if (TYPES.DOWNLOAD_PRINTING_SUMMARY === type) {
      return {
        off: !isCampaignCreatedFromCampaignBuilder,
        [BUTTON_TYPES.DOWNLOAD]: {
          label: 'Download',
          onClick: async () => {
            await downloadSummary({
              campaignId,
              countryId,
              campaignType,
              campaignName,
              clientCodes,
              internalCodes,
            })
          },
          disabled: !isCampaignCreatedFromCampaignBuilder || !isHaveCodes,
        },
      }
    }
  }

  useEffect(() => {
    if (isLoaded) {
      const initialContracts = {
        [CAMPAIGN_DOCUMENT_TYPES.DIGITAL_PROOF_FROM_PRINTER]: null,
        [CAMPAIGN_DOCUMENT_TYPES.DIGITAL_PROOF_SIGNED_BY_CLIENT]: null,
        [CAMPAIGN_DOCUMENT_TYPES.STATEMENT_OF_WORK]: null,
      }

      const types = Object.keys(initialContracts)
      const contracts = types.reduce((acc, item) => {
        acc[item] = data.contracts[item]
        return acc
      }, initialContracts)

      const buttonActions = Object.keys(TYPES).reduce((acc, key) => {
        acc[key] = getButtons({
          contracts,
          type: key,
          campaign: { campaignId, ...data.campaign },
        })
        return acc
      }, {})

      setCampaign({ ...data.campaign, contracts: data.contracts })
      setButtonsActions(buttonActions)
      setIsLoading(false)
    }
  }, [isLoaded])

  return isLoading ? (
    <LoadingIndicator style={{ height: '100vh' }} />
  ) : (
    <Fragment>
      {isDownloading ? <Loader /> : null}
      <InternalFilesView buttonsActions={buttonsActions} campaign={campaign} />
    </Fragment>
  )
}
export default InternalFilesContainer
